import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Grid, TextField, InputAdornment, Typography } from '@material-ui/core';
import Card from 'components/Card';
import Loader from 'components/Loader';
import Input from 'components/Input';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';

import { isValidBarCode } from 'helpers/common';
import useConfirm from 'helpers/hooks/useConfirm';

import { cosmos } from 'services/external/cosmos';

import { setProductBasic, setProductIdentifiers, setProductProvisionalReceipt } from '../../../store/actions';

export default function Identifiers({ formikProps }) {
  const dispatch = useDispatch();

  const { isLoading, provisionalReceipt } = useSelector(state => state.productsSellRegistration);

  const { values, errors, handleChange, setFieldValue } = formikProps;

  const setFieldValueRef = useRef(setFieldValue);
  setFieldValueRef.current = setFieldValue;

  const valuesRef = useRef(values);
  valuesRef.current = values;

  useEffect(() => {
    return () => {
      const currentValues = valuesRef.current;

      if(currentValues?.internalCode || currentValues?.barCode || currentValues?.gtinEan) {
        dispatch(setProductIdentifiers({
          internalCode: currentValues?.internalCode || '',
          barCode: currentValues?.barCode || '',
          gtinEan: currentValues?.gtinEan || ''
        }));
      }
    }
  }, [dispatch]);

  const [ConfirmModal, confirm] = useConfirm(
    'Código de barra',
    'Deseja autopreencher os dados do produto?'
  );

  const getAutofillPreview = data => {
    const fields = [
      { label: 'Nome do Produto', value: data?.name },
      { label: 'Código GTIN/EAN', value: data?.gtinEan },
      { label: 'NCM', value: data?.ncmCode }
    ]?.filter(field => field?.value);

    if(!fields?.length) {
      return null;
    }

    return(
      <Grid container style={{ backgroundColor: '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 8, padding: 10, marginTop: 16 }}>
        {fields?.map(field => (
          <Grid item xs={12} key={`${field?.step}-${field?.label}`} style={{ marginBottom: 8 }}>
            <Typography variant="body2" color="textSecondary">
              {field?.label}
            </Typography>
            <Typography variant="body1">
              {field?.value}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  }

  const fillProductData = data => {
    if(data?.name) {
      dispatch(setProductBasic({ name: data?.name }));
    }

    if(data?.gtinEan) {
      setFieldValue('gtinEan', data?.gtinEan);
      dispatch(setProductIdentifiers({ gtinEan: data?.gtinEan }));
    }

    if(data?.ncmCode) {
      dispatch(setProductProvisionalReceipt({
        ncm: data?.ncmCode,
        cfop: provisionalReceipt?.cfopCode,
        icmsOriginId: provisionalReceipt?.icmsOriginId,
        icmsCst: provisionalReceipt?.icmsCst
      }));
    }
  }

  const handleSearchBarCode = async barCode => {
    dispatch(setProductIdentifiers({ barCode }));

    try {
      const search = await cosmos.get(`/gtins/${barCode}`);

      if(search?.status == 200) {
        const { description, gtin, ncm } = search?.data || {};

        const data = {
          name: description,
          gtinEan: gtin,
          ncmCode: ncm?.code
        };

        const shouldFill = await confirm({
          content: getAutofillPreview(data)
        });

        if(shouldFill) {
          fillProductData(data);
        }
      }
    } catch {
      toast.error('Não foi possível consultar o código de barra.');
    }
  }

  const handleSearchBarCodeRef = useRef(handleSearchBarCode);
  handleSearchBarCodeRef.current = handleSearchBarCode;

  useEffect(() => {
    let buffer = '';
    let lastKeyTime = 0;

    const handleKeyDown = (event) => {
      const now = Date.now();
      const isRapid = now - lastKeyTime < 50;

      lastKeyTime = now;

      if(event.key == 'Enter') {
        if(buffer.length > 1) {
          event.preventDefault();
          event.stopPropagation();

          if(isValidBarCode(buffer)) {
            handleSearchBarCodeRef.current(buffer);
            setFieldValueRef.current('barCode', buffer);
            toast.success('Código de barra lido com sucesso!');
          } else {
            toast.error('Não foi possível ler o código de barra.');
          }
        }

        buffer = '';

        return;
      }

      if(event.key.length == 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if(!isRapid) {
          buffer = event.key;
          return;
        }

        buffer += event.key;

        event.preventDefault();
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  return(
    <>
      <ConfirmModal />
      <Card title="Preencha os códigos de identificadores e classificação do produto">
        <Grid container>
          <Grid xs={12}>
            <Loader isLoading={isLoading}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Input
                    name="internalCode"
                    type="text"
                    label="Código do Produto (SKU)"
                    value={values?.internalCode || ''}
                    error={errors?.internalCode}
                    helperText={errors?.internalCode}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="barCode"
                    type="text"
                    label="Código de Barra Interno"
                    value={values?.barCode || ''}
                    error={errors?.barCode}
                    helperText={errors?.barCode}
                    onChange={handleChange}
                    onKeyDown={event => {
                      if(event.key == 'Enter') {
                        event.preventDefault();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <FontAwesomeIcon
                            size="lg"
                            color="#022A5C"
                            icon={faBarcode}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Input
                    name="gtinEan"
                    type="text"
                    label="Código GTIN/EAN"
                    value={values?.gtinEan || ''}
                    error={errors?.gtinEan}
                    helperText={errors?.gtinEan}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Loader>
          </Grid>
        </Grid>
      </Card>
    </>
  );
}